import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { BaseMUIComponentProps, ErrorStatus } from '../types/Types';

export interface DropdownStringSelectorProps extends BaseMUIComponentProps {
  initialSelectedString?: string | null;
  onSelectString: (value: string | null) => void;
  values: string[];
  name?: string;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  errors?: ErrorStatus;
}

/**
 * DropdownStringSelector
 *
 * A dropdown component based on MUI's `Autocomplete` that allows selecting or entering a string value
 *
 * - @param initialSelectedItem: The initial selected value in the dropdown.
 * - @param onSelectString: Callback function triggered when a value is selected or cleared.
 * - @param values: The list of selectable string options.
 * - @param name: Name attribute for the input, useful for form handling.
 * - @param open: Optional external control for the dropdown’s open/closed state.
 * - @param setOpen: Function to control the open state externally.
 * - @param errors: Object used to display validation messages after form submission.
 */
export function DropdownStringSelector({
  initialSelectedString: defaultValue = null,
  onSelectString,
  values,
  name,
  open,
  setOpen,
  errors,
  ...props
}: DropdownStringSelectorProps) {
  const error =
    errors && errors.tag === 'Submited' && errors.errors.length !== 0;
  const helperText =
    errors && errors.tag === 'Submited' ? errors.errors.join(', ') : '';

  return (
    <Autocomplete
      freeSolo
      disablePortal
      options={values}
      getOptionLabel={(option) => option}
      onChange={(_, value) => onSelectString(value ?? null)}
      value={defaultValue ?? null}
      className={props.className}
      disabled={props.disabled}
      open={open}
      onOpen={setOpen ? () => setOpen(true) : undefined}
      onClose={setOpen ? () => setOpen(false) : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          required={props.required}
          name={name}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
}

export default DropdownStringSelector;
